import { request as http } from '@/utils/http'
import type { PageResponse } from '@/api/types/common'
import type { AxiosRequestConfig } from 'axios'

const SPECIAL_PRICE_APPLICATION_API_BASE = '/price/special-price/application'
const SILENT_ERROR_CONFIG: AxiosRequestConfig & { showErrorMessage: false } = {
  showErrorMessage: false
}

export type SpecialPriceRateType = 'FCL' | 'LCL' | 'PRE_PORT' | 'LAST_MILE' | 'AIR' | 'EXPRESS' | 'RAIL'

export const SPECIAL_PRICE_APPLICATION_STATUS = {
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  EXPIRED: 'EXPIRED',
  ORDERED: 'ORDERED'
} as const

export type SpecialPriceApplicationStatus =
  (typeof SPECIAL_PRICE_APPLICATION_STATUS)[keyof typeof SPECIAL_PRICE_APPLICATION_STATUS]

export interface SpecialPriceApplicationPriceItemRequest {
  itemCode: string
  itemName?: string
  quantity: number
  targetUnitPrice: number
  originalUnitPrice?: number
  targetMinCharge?: number
  originalMinCharge?: number
  currencyId?: string | number
  currencyCode?: string
  billingUnitId?: string | number
  densityLevel?: string
  pricingTierCode?: string
}

export interface SpecialPriceApplicationScheduleInfoRequest {
  scheduleId: string
  vessel?: string
  voyage?: string
  laneCode?: string
  etd?: string
  eta?: string
  source?: string
}

export interface SpecialPriceApplicationQuantityDetail {
  volumeCbm?: number | string | null
  weightTon?: number | string | null
}

export interface SpecialPriceApplicationSubmitRequest {
  rateType: SpecialPriceRateType
  rateId: string | number
  rateCompositeId?: string
  customerId: string | number
  cargoReadyDate: string
  cargoType: string | number
  reason: string
  remark?: string
  specifySchedule?: boolean
  scheduleInfo?: SpecialPriceApplicationScheduleInfoRequest
  quantityDetail?: SpecialPriceApplicationQuantityDetail
  priceItems: SpecialPriceApplicationPriceItemRequest[]
  rateSnapshot?: Record<string, unknown>
}

export interface SpecialPriceApplicationSubmitVO {
  id: string | number
  applicationNo: string
  status: string
  documentProcessId?: string | number | null
}

export interface SpecialPriceOrderEligibilityVO {
  applicationId: string
  status?: SpecialPriceApplicationStatus | string | null
  canOrder?: boolean | null
  orderId?: string | null
  expireAt?: string | null
}

export interface SpecialPriceOrderPriceItemVO {
  itemCode?: string | null
  itemName?: string | null
  quantity?: number | string | null
  targetUnitPrice?: number | string | null
  originalUnitPrice?: number | string | null
  targetMinCharge?: number | string | null
  originalMinCharge?: number | string | null
  currencyId?: string | null
  currencyCode?: string | null
  billingUnitId?: string | null
  densityLevel?: string | null
  pricingTierCode?: string | null
}

export interface SpecialPriceOrderScheduleVO {
  scheduleId?: string | null
  vessel?: string | null
  voyage?: string | null
  laneCode?: string | null
  etd?: string | null
  eta?: string | null
  source?: string | null
}

export interface SpecialPriceOrderPrepareVO {
  applicationId: string
  applicationNo?: string | null
  rateType?: SpecialPriceRateType | null
  rateId?: string | null
  rateNo?: string | null
  customerId?: string | null
  customerName?: string | null
  supplierId?: string | null
  carrierId?: string | null
  pol?: string | null
  pod?: string | null
  cargoReadyDate?: string | null
  cargoType?: string | null
  specifySchedule?: boolean | null
  scheduleInfo?: SpecialPriceOrderScheduleVO | null
  quantityDetail?: SpecialPriceApplicationQuantityDetail | null
  priceItems?: SpecialPriceOrderPriceItemVO[] | null
  rateSnapshot?: Record<string, unknown> | null
  status?: SpecialPriceApplicationStatus | string | null
  expireAt?: string | null
  orderId?: string | null
}

export interface SpecialPriceApprovalDetailRequest {
  applicationId?: string | number | null
  documentProcessId?: string | number | null
}

export interface SpecialPriceApplicationPriceItemVO {
  itemCode?: string | null
  itemName?: string | null
  quantity?: number | string | null
  targetUnitPrice?: number | string | null
  originalUnitPrice?: number | string | null
  targetMinCharge?: number | string | null
  originalMinCharge?: number | string | null
  currencyId?: string | number | null
  currencyCode?: string | null
  billingUnitId?: string | number | null
  densityLevel?: string | null
  pricingTierCode?: string | null
}

export interface SpecialPriceApplicationScheduleInfoVO {
  scheduleId?: string | null
  vessel?: string | null
  voyage?: string | null
  laneCode?: string | null
  etd?: string | null
  eta?: string | null
  source?: string | null
}

export interface SpecialPriceCustomerHistoryRateVO {
  orderId?: string | number | null
  orderNo?: string | null
  dealDate?: string | null
  rateType?: SpecialPriceRateType | string | null
  pol?: string | number | null
  pod?: string | number | null
  feeId?: string | number | null
  measureId?: string | number | null
  measureName?: string | null
  unitPrice?: number | string | null
  quantity?: number | string | null
  inclusiveTaxPrice?: number | string | null
  currencyCode?: string | null
}

export interface SpecialPriceCustomerHistoryApplicationVO {
  applicationId?: string | number | null
  applicationNo?: string | null
  dateCreated?: string | null
  rateType?: SpecialPriceRateType | string | null
  pol?: string | number | null
  pod?: string | number | null
  targetAmount?: number | string | null
  currencyId?: string | number | null
  status?: string | null
}

export interface SpecialPriceApplicationStatisticsVO {
  totalApplications?: number | string | null
  approvedCount?: number | string | null
  rejectedCount?: number | string | null
  passRate?: number | string | null
}

export interface SpecialPriceCustomerProfileVO {
  customerId?: string | number | null
  customerName?: string | null
  totalOrderCount?: number | string | null
  recentSixMonthAmount?: number | string | null
  recentSixMonthCurrencyCode?: string | null
  specialPriceHistory?: SpecialPriceApplicationStatisticsVO | null
}

export interface SpecialPriceCustomerHistoryPageRequest {
  applicationId: string | number
  pageNum: number
  pageSize: number
}

export interface SpecialPricePriceDeviationItemVO {
  available?: boolean | null
  unavailableReason?: string | null
  itemCode?: string | null
  itemName?: string | null
  quantity?: number | string | null
  targetUnitPrice?: number | string | null
  originalUnitPrice?: number | string | null
  currencyId?: string | number | null
  currencyCode?: string | null
  billingUnitId?: string | number | null
  deviationRate?: number | string | null
  deviationAmount?: number | string | null
}

export interface SpecialPricePriceDeviationSummaryVO {
  currencyId?: string | number | null
  currencyCode?: string | null
  originalAmount?: number | string | null
  targetAmount?: number | string | null
  deviationAmount?: number | string | null
  deviationRate?: number | string | null
}

export interface SpecialPricePriceDeviationVO {
  available?: boolean | null
  items?: SpecialPricePriceDeviationItemVO[] | null
  summaries?: SpecialPricePriceDeviationSummaryVO[] | null
}

export interface SpecialPriceOrderCancellationRiskVO {
  statisticsAvailable?: boolean | null
  recentThreeMonthOrderCount?: number | string | null
  recentThreeMonthCancelledOrderCount?: number | string | null
  recentThreeMonthCancellationRate?: number | string | null
}

export interface SpecialPriceDecisionSupportVO {
  priceDeviation?: SpecialPricePriceDeviationVO | null
  orderCancellationRisk?: SpecialPriceOrderCancellationRiskVO | null
}

export interface SpecialPriceApprovalDetailVO {
  id?: string | number | null
  applicationNo?: string | null
  rateType?: SpecialPriceRateType | string | null
  rateId?: string | number | null
  rateNo?: string | null
  customerId?: string | number | null
  customerName?: string | null
  applicantId?: string | number | null
  applicantName?: string | null
  officeId?: string | number | null
  supplierId?: string | number | null
  carrierId?: string | number | null
  pol?: string | number | null
  pod?: string | number | null
  rateBeginDate?: string | null
  rateEndDate?: string | null
  cargoReadyDate?: string | null
  cargoType?: string | number | null
  reason?: string | null
  remark?: string | null
  specifySchedule?: boolean | null
  scheduleId?: string | null
  scheduleInfo?: SpecialPriceApplicationScheduleInfoVO | null
  quantityDetail?: SpecialPriceApplicationQuantityDetail | null
  priceItems?: SpecialPriceApplicationPriceItemVO[] | null
  targetAmount?: number | string | null
  currencyId?: string | number | null
  rateSnapshot?: Record<string, unknown> | null
  customerProfile?: SpecialPriceCustomerProfileVO | null
  status?: string | null
  documentProcessId?: string | number | null
  approvedAt?: string | null
  expireAt?: string | null
  rejectedReason?: string | null
  orderId?: string | number | null
  orderedAt?: string | null
  dateCreated?: string | null
  dateModified?: string | null
}

/**
 * 提交 FCL/LCL 特价申请单。
 */
export const submitSpecialPriceApplication = (params: SpecialPriceApplicationSubmitRequest) => {
  return http.post<SpecialPriceApplicationSubmitVO>(
    `${SPECIAL_PRICE_APPLICATION_API_BASE}/submit`,
    params
  )
}

/**
 * 批量查询当前用户发起的特价申请下单资格。
 */
export const batchGetSpecialPriceOrderEligibility = (applicationIds: string[]) => {
  return http.post<SpecialPriceOrderEligibilityVO[]>(
    `${SPECIAL_PRICE_APPLICATION_API_BASE}/order-eligibility/batch`,
    { applicationIds }
  )
}

/**
 * 校验特价申请并获取订舱页所需的受控快照。
 */
export const prepareSpecialPriceOrder = (applicationId: string) => {
  return http.post<SpecialPriceOrderPrepareVO>(
    `${SPECIAL_PRICE_APPLICATION_API_BASE}/order-prepare`,
    { applicationId }
  )
}

/**
 * 查询特价申请审批详情，支持按申请单 ID 或 BPM 流程实例 ID 定位。
 */
export const getSpecialPriceApprovalDetail = (params: SpecialPriceApprovalDetailRequest) => {
  return http.post<SpecialPriceApprovalDetailVO>(
    `${SPECIAL_PRICE_APPLICATION_API_BASE}/approval-detail`,
    params
  )
}

/**
 * 查询特价申请审批客户画像。
 */
export const getSpecialPriceCustomerProfile = (params: SpecialPriceApprovalDetailRequest) => {
  return http.post<SpecialPriceCustomerProfileVO>(
    `${SPECIAL_PRICE_APPLICATION_API_BASE}/customer-profile`,
    params,
    SILENT_ERROR_CONFIG
  )
}

/**
 * 查询特价申请审批辅助决策信息。
 */
export const getSpecialPriceDecisionSupport = (params: SpecialPriceApprovalDetailRequest) => {
  return http.post<SpecialPriceDecisionSupportVO>(
    `${SPECIAL_PRICE_APPLICATION_API_BASE}/decision-support`,
    params,
    SILENT_ERROR_CONFIG
  )
}

/**
 * 分页查询当前申请对应客户、航线和运价类型的历史成交运价。
 */
export const pageSpecialPriceCustomerHistoryRates = (
  params: SpecialPriceCustomerHistoryPageRequest
) => {
  return http.post<PageResponse<SpecialPriceCustomerHistoryRateVO>>(
    `${SPECIAL_PRICE_APPLICATION_API_BASE}/customer-history-rates/page`,
    params,
    SILENT_ERROR_CONFIG
  )
}

/**
 * 分页查询当前申请对应客户近六个月的历史特价申请。
 */
export const pageSpecialPriceCustomerHistoryApplications = (
  params: SpecialPriceCustomerHistoryPageRequest
) => {
  return http.post<PageResponse<SpecialPriceCustomerHistoryApplicationVO>>(
    `${SPECIAL_PRICE_APPLICATION_API_BASE}/customer-history-applications/page`,
    params,
    SILENT_ERROR_CONFIG
  )
}
